import { z } from "zod";

export const organizationStatusSchema = z.enum([
  "Evaluation",
  "Active",
  "PoorStanding",
  "Internal",
  "Inactive",
]);
// Base schemas
export const organizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  scopes: z
    .array(z.string())
    .nullish()
    .transform((s) => s ?? []),
  status: organizationStatusSchema,
  // logto_id: z.string().nullable().optional(),
  source: z.enum(["LOGTO", "CUSTOM"]),
  created_at: z.coerce.date(),
  updated_at: z.coerce.date().optional(),
  last_used_at: z.coerce.date().nullable().optional(),
  api_key_count: z.number().optional(),
  current_monthly_usage: z.number().optional(),
});
// Base quota data with core fields
// Dynamic permission fields (allow_*) are preserved during parsing
export const baseQuotaData = z.object({
  daily_parcels: z.number().nullable(),
  monthly_parcels: z.number().nullable(),
  priority: z.number(),
  start_date: z.coerce.date(),
  end_date: z.coerce.date().nullish(),
  is_active: z.boolean().default(true),
}).loose();

export const quotaSchema = baseQuotaData.extend({
  id: z.string(),
  organization_id: z.string(),
  enable_expiration_reminder: z.boolean().default(true),
  is_current: z.boolean().optional(),
  updated_at: z.coerce.date(),
}).loose();


 


export const apiKeySchema = z.object({
  key_id: z.string(),
  organization_id: z.string(),
  name: z.string().nullish(),
  description: z.string().nullish(),
  api_key: z.string().optional(), // Only returned on creation
  expires_at: z.coerce.date().nullable().optional(),
  expires_in: z.number().nullable().optional(),
  is_active: z.boolean().default(false),
  created_at: z.coerce.date(),
  last_used_at: z.coerce.date().nullable().optional(),
  scopes: z
    .array(z.string())
    .nullish()
    .transform((s) => s ?? []),
});

// Usage tracking schemas
export const usageDataSchema = z.object({
  id: z.string(),
  organization_id: z.string(),
  date: z.coerce.date(),
  data_type: z.enum([
    "address",
    "assessor",
    "listing",
    "recorder",
    "financial",
    "sale_estimate",
    "rent_estimate",
    "comps",
  ]),
  count: z.number(),
  created_at: z.coerce.date(),
});


// Daily quota usage with core fields plus dynamic quota type fields
// Quota type fields (assessor, listing, etc.) are dynamically determined by the API
// Dynamic fields are preserved during parsing
export const quotaUsageDailySchema = z.object({
  id: z.string(),
  organization_id: z.string(),
  date: z.coerce.date(),
  parcels: z.number(), // Total parcels is always present
}).loose();

// Monthly quota usage with core fields plus dynamic quota type fields
// Quota type fields (assessor, listing, etc.) are dynamically determined by the API
// Dynamic fields are preserved during parsing
export const quotaUsageMonthlySchema = z.object({
  id: z.string(),
  organization_id: z.string(),
  month: z.number(),
  year: z.number(),
  parcels: z.number(), // Total parcels is always present
}).loose();

// export const dailyUsageSchema = z.object({
//   date: z.coerce.date(),
//   organization_id: z.string(),
//   // organization_name: z.string().optional(),
//   address: z.number(),
//   assessor: z.number(),
//   listing: z.number(),
//   recorder: z.number(),
//   sale_estimate: z.number(),
//   rent_estimate: z.number(),
//   // comps: z.number(),
//   rent_comps: z.number().optional(),
//   sale_comps: z.number().optional(),
//   parcels: z.number(),
// })

// export const monthlyUsageSchema = z.object({
//   year: z.number(),
//   month: z.number(),
//   organization_id: z.string(),
//   // organization_name: z.string().optional(),
//   // total_parcels: z.number().optional(),
//   address: z.number(),
//   assessor: z.number(),
//   listing: z.number(),
//   recorder: z.number(),
//   sale_estimate: z.number(),
//   rent_estimate: z.number(),
//   comps: z.number(),
//   rent_comps: z.number().optional(),
//   sale_comps: z.number().optional(),
//   parcels: z.number(),
// })

// Request schemas for API calls
export const createOrganizationRequestSchema = z.object({
  name: z.string(),
  scopes: z.array(z.string()).optional(),
  evaluation_mode: z.boolean().optional(),
  logto_id: z.string().optional(),
  create_api_key: z.boolean().optional(),
  quota: baseQuotaData
    .optional(),
});

export const createApiKeyRequestSchema = z.object({
  organization_id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  expires_in: z.number().optional(),
  scopes: z.array(z.string()).optional(),
});

export const importApiKeyRequestSchema = z.object({
  organization_id: z.string(),
  api_key: z.string(),
  name: z.string(),
  description: z.string().optional(),
  expires_in: z.number().optional(),
  scopes: z.array(z.string()).optional(),
});

export const addApiKeyRequestSchema = z.object({
  organization_id: z.string(),
  api_key: z.string(),
  name: z.string(),
  description: z.string().optional(),
  expires_in: z.number().optional(),
  scopes: z.array(z.string()).optional(),
});

export const updateOrganizationQuotaRequestSchema = z.object({
  evaluation_mode: z.boolean().optional(),
  scopes: z.array(z.string()).optional(),
  quota: baseQuotaData
});

export const createQuotaRequestSchema = baseQuotaData.extend({
    organization_id: z.string(),
    enable_expiration_reminder: z.boolean().default(true),
    is_active: z.boolean().optional(),
});

export const updateQuotaRequestSchema = baseQuotaData.extend({
  quota_id: z.string(),
  enable_expiration_reminder: z.boolean().default(true),
  is_active: z.boolean().optional(),
});

// API Response schemas
export const apiResponseSchema = z.object({
  data: z.any(),
  message: z.string().optional(),
});

export const errorResponseSchema = z.object({
  detail: z.string(),
  trace_id: z.string(),
});

// Organization roles enum
export const organizationRoleSchema = z.enum([
  "Owner",
  "Admin",
  "Developer",
  "Reader",
  "Playground",
]);

// Organization user schema (our application-specific format)
export const organizationUserSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  primaryEmail: z.email().nullable(),
  primaryPhone: z.string().nullable(),
  username: z.string().nullable(),
  avatar: z.string().nullable(),
  roles: z.array(organizationRoleSchema),
  createdAt: z.date(),
  lastSignInAt: z.date().nullable(),
  status: z.enum(["active", "suspended", "pending"]),
});

// Organization invitation schemas (OSS v1.26.0- approach)
export const organizationInvitationSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  email: z.email(),
  roles: z.array(organizationRoleSchema),
  status: z.enum(["Pending", "Accepted", "Expired", "Revoked"]),
  createdAt: z.number(),
  updatedAt: z.number(),
  expiresAt: z.number(),
  inviterId: z.string().nullable(),
  inviterUser: z
    .object({
      id: z.string(),
      name: z.string().nullable(),
      primaryEmail: z.email().nullable(),
      username: z.string().nullable(),
      avatar: z.string().nullable(),
    })
    .optional(),
  organization: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .optional(),
  // acceptedAt: z.number().nullable(),
});

// Inferred types from schemas
export type Organization = z.infer<typeof organizationSchema>;
export type OrganizationStatus = z.infer<typeof organizationStatusSchema>;
// Quota types with support for dynamic permission fields
export type Quota = z.infer<typeof quotaSchema> & {
  [x: string]: any; // Allow dynamic permission fields (allow_*)
};
export type QuotaWithActive = Quota & { isActive: boolean };
export type ApiKey = z.infer<typeof apiKeySchema>;
export type UsageData = z.infer<typeof usageDataSchema>;

// Usage types with support for dynamic quota type fields
export type QuotaUsageDaily = z.infer<typeof quotaUsageDailySchema> & {
  [x: string]: any; // Allow dynamic quota type fields (assessor, listing, etc.)
};
export type QuotaUsageMonthly = z.infer<typeof quotaUsageMonthlySchema> & {
  [x: string]: any; // Allow dynamic quota type fields (assessor, listing, etc.)
};
export type OrganizationRole = z.infer<typeof organizationRoleSchema>;
export type OrganizationUser = z.infer<typeof organizationUserSchema>;
export type OrganizationInvitation = z.infer<
  typeof organizationInvitationSchema
>;
// export type DailyUsage = z.infer<typeof dailyUsageSchema>
// export type MonthlyUsage = z.infer<typeof monthlyUsageSchema>

// Request types
export type CreateOrganizationRequest = z.infer<
  typeof createOrganizationRequestSchema
>;
export type CreateApiKeyRequest = z.infer<typeof createApiKeyRequestSchema>;
export type ImportApiKeyRequest = z.infer<typeof importApiKeyRequestSchema>;
export type AddApiKeyRequest = z.infer<typeof addApiKeyRequestSchema>;
export type UpdateOrganizationQuotaRequest = z.infer<
  typeof updateOrganizationQuotaRequestSchema
> & {
  [x: string]: any; // Allow dynamic permission fields
};
export type CreateQuotaRequest = z.infer<typeof createQuotaRequestSchema> & {
  [x: string]: any; // Allow dynamic permission fields
};
export type UpdateQuotaRequest = z.infer<typeof updateQuotaRequestSchema> & {
  [x: string]: any; // Allow dynamic permission fields
};

// API Response types
export type ApiResponse<T> = {
  data: T;
  message?: string;
};

export type ErrorResponse = z.infer<typeof errorResponseSchema>;

// Skill tool example — structured call example attached to a skill document
export const toolExampleSchema = z.object({
  tool_name: z.string(),
  description: z.string(),
  inputs: z.record(z.string(), z.any()),
});

export const skillConfigSchema = z.object({
  tool_names: z.array(z.string()),
  examples: z.array(toolExampleSchema),
});

// Document schemas
export const documentMetadataSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  description: z.string().default(""),
  author: z.string().optional(),
  icon_id: z.string().optional(),
  icon_color: z.string().optional(),
  folder_ids: z.array(z.string()).default([]),
  is_guide: z.boolean().default(false),
  is_skill: z.boolean().default(false),
  /** Hidden from sidebar/search; published docs stay reachable via direct link */
  is_unlisted: z.boolean().default(false),
  priority: z.number().default(0),
  content_type: z.literal("application/json"),
  size: z.number(),
  created_at: z.coerce.date(),
  updated_at: z.coerce.date(),
  published_at: z.coerce.date().nullable().optional(),
  /** True when published content exists but draft has unpromoted changes */
  has_unpublished_changes: z.boolean().default(false),
  etag: z.string(),
  skill_config: skillConfigSchema.optional(),
});

export const folderMetadataSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  icon_id: z.string().optional(),
  icon_color: z.string().optional(),
  parent_id: z.string().nullable().default(null),
  priority: z.number().default(0),
  created_at: z.coerce.date(),
  updated_at: z.coerce.date(),
});

export const imageMetadataSchema = z.object({
  id: z.string(),
  name: z.string(),
  path: z.string(),
  document_id: z.string(),
  content_type: z.string(),
  size: z.number(),
  created_at: z.coerce.date(),
  etag: z.string(),
});

// Tiptap content schema
export const tiptapContentSchema = z.object({
  type: z.literal("doc"),
  content: z.array(z.any()), // Tiptap node structure
});

// Document content schema (now uses Tiptap format)
export const documentContentSchema = tiptapContentSchema;

// Export types
export type DocumentContent = z.infer<typeof documentContentSchema>;

// Document operation schemas
export const getDocumentSchema = z.object({
  slug: z.string(),
});

export const saveDocumentSchema = z.object({
  id: z.string().optional(),
  slug: z.string(),
  name: z.string(),
  description: z.string().default(""),
  author: z.string().optional(),
  icon_id: z.string().optional(),
  icon_color: z.string().optional(),
  folder_ids: z.array(z.string()).default([]),
  is_guide: z.boolean().default(false),
  is_skill: z.boolean().default(false),
  is_unlisted: z.boolean().default(false),
  priority: z.number().default(0),
  content: documentContentSchema,
  skill_config: skillConfigSchema.optional(),
});

export const saveFolderSchema = z.object({
  id: z.string().optional(),
  name: z.string(),
  slug: z.string(),
  icon_id: z.string().optional(),
  icon_color: z.string().optional(),
  parent_id: z.string().nullable().default(null),
});

export const uploadImageSchema = z.object({
  document_id: z.string(),
  file: z.any(), // Will be handled as FormData
  filename: z.string(),
});

/** Normalise the API's snake_case `is_deprecated` to camelCase so the rest of the frontend stays consistent. */
function normaliseDynamicConfigItem(val: unknown) {
  if (val && typeof val === "object") {
    const obj = val as Record<string, unknown>;
    if ("is_deprecated" in obj && !("isDeprecated" in obj)) {
      const { is_deprecated, ...rest } = obj;
      return { ...rest, isDeprecated: is_deprecated };
    }
  }
  return val;
}

export const dynamicConfigItem = z.preprocess(
  normaliseDynamicConfigItem,
  z.object({
    friendlyName: z.string(),
    key: z.string(),
    description: z.string().optional(),
    group: z.string().optional(),
    isDeprecated: z.boolean().optional(),
    linked_usage_type_key: z.string().optional(),
  }),
);

/** Upstream dynamic-config items; visibility flags apply to non-admin viewers only (enforced in BFF). */
export const dynamicConfigItemInternalSchema = z.preprocess(
  normaliseDynamicConfigItem,
  z.object({
    friendlyName: z.string(),
    key: z.string(),
    description: z.string().optional(),
    group: z.string().optional(),
    isDeprecated: z.boolean().optional(),
    always_hide: z.boolean().optional().default(false),
    hide_when_missing: z.boolean().optional().default(false),
    /** Quota JSON key that must be true when hide_when_missing (quotaTypes only). Empty = not gated. */
    quota_allow_key: z.string().optional().default(""),
    /** Usage meter key tied to this allow permission (usagePermissions only). */
    linked_usage_type_key: z.string().optional().default(""),
  }),
);

export const dynamicConfigInternalSchema = z.object({
  quotaTypes: z.array(dynamicConfigItemInternalSchema),
  usagePermissions: z.array(dynamicConfigItemInternalSchema),
});

export const dynamicConfigSchema = z.object({
  quotaTypes: z.array(dynamicConfigItem),
  usagePermissions: z.array(dynamicConfigItem),
});

// Export types
export type ToolExample = z.infer<typeof toolExampleSchema>;
export type SkillConfig = z.infer<typeof skillConfigSchema>;
export type DocumentMetadata = z.infer<typeof documentMetadataSchema>;
export type FolderMetadata = z.infer<typeof folderMetadataSchema>;
export type ImageMetadata = z.infer<typeof imageMetadataSchema>;
export type SaveDocumentRequest = z.infer<typeof saveDocumentSchema>;
export type SaveFolderRequest = z.infer<typeof saveFolderSchema>;
export type UploadImageRequest = z.infer<typeof uploadImageSchema>;
export type DynamicConfig = z.infer<typeof dynamicConfigSchema>;
export type DynamicConfigInternal = z.infer<typeof dynamicConfigInternalSchema>;

/** Daily/monthly usage API: rows plus labels for meters that may be omitted from filtered dynamic config. */
export type UsageSeriesWithLabels<T> = {
  rows: T[];
  usageQuotaTypeLabels: Record<string, string>;
};
